import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import axios from 'axios';
import {
    Table,
    Tag,
    Button,
    Card,
    Space,
    Typography,
    Alert,
    Spin,
    Tooltip,
    Descriptions,
    Badge,
    message,
    Image,
    Modal,
    Input,
    Timeline,
    Select
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    UserOutlined,
    ReloadOutlined,
    EyeOutlined,
    CloseOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import dayjs from "dayjs";
import * as XLSX from "xlsx";


const { Title, Text } = Typography;
const { TextArea } = Input;

export default function PastOnduty() {
    const [ondutyrequests, setOndutyrequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    // Target row to highlight (from notification)
    const [targetOndutyId, setTargetOndutyId] = useState(null);
    const highlightRowRef = useRef(null);

    const [isEmployeeView, setIsEmployeeView] = useState(
        localStorage.getItem("Hrselected") === "employee"
    );

    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    // Status filter state - default PENDING
    const [statusFilter, setStatusFilter] = useState('PENDING');

    // Counts for each status
    const [statusCounts, setStatusCounts] = useState({
        PENDING: 0,
        APPROVED: 0,
        REJECTED: 0,
    });

    // Search/filter state
    const [filters, setFilters] = useState({
        employee_name: '',
        employee_code: '',
        type: '',
        remarks: '',
        start_date: '',
        end_date: '',
        month: null,
        year: null,
    });

    // ✅ NEW: keep a ref in sync with `filters` so the fetch functions can
    // always read the *latest* filter values without needing `filters` in
    // their own dependency arrays. That's what lets us stop auto-fetching
    // on every keystroke — fetching now only happens when we explicitly
    // call fetchOndutyrequests() (Filter button, Clear button, tab change).
    const filtersRef = useRef(filters);
    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    // Selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

    // Modal states
    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [isBulkApproveModalVisible, setIsBulkApproveModalVisible] = useState(false);
    const [isBulkRejectModalVisible, setIsBulkRejectModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [approveComments, setApproveComments] = useState('');
    const [rejectComments, setRejectComments] = useState('');
    const [bulkApproveComments, setBulkApproveComments] = useState('');
    const [bulkRejectComments, setBulkRejectComments] = useState('');

    // Determine if current user is a regular employee (role === 2)
    const isRegularEmployee = authData?.employee?.role === 2;

    useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [ondutyrequests]);



    const [isMyTurnOnly, setIsMyTurnOnly] = useState(false);

    // ✅ Handle navigation from notifications - highlight specific on-duty row & set status filter
    useEffect(() => {
        if (navigationParams?.status) {
            console.log("🎯 PastOnduty: Setting status filter from navigationParams:", navigationParams.status);
            setStatusFilter(navigationParams.status.toUpperCase());
        }

        if (navigationParams?.my_turn !== undefined) {
            setIsMyTurnOnly(Boolean(navigationParams.my_turn));
        }

        if (navigationParams?.ondutyId && navigationParams?.openModal) {
            console.log("🎯 PastOnduty: Received navigation params for ID highlight:", navigationParams);
            setTargetOndutyId(navigationParams.ondutyId);
        }

        if (navigationParams?.status || navigationParams?.my_turn !== undefined || (navigationParams?.ondutyId && navigationParams?.openModal)) {
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Scroll to highlighted row after ondutyrequests load
    useEffect(() => {
        if (targetOndutyId && ondutyrequests.length > 0) {
            const targetIdNum = parseInt(targetOndutyId, 10);
            const found = ondutyrequests.find((r) => r.id === targetIdNum);
            if (found) {
                console.log("🎯 PastOnduty: Found target on-duty:", found);
                setTimeout(() => {
                    if (highlightRowRef.current) {
                        highlightRowRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 300);
            }
        }
    }, [targetOndutyId, ondutyrequests]);

    /**
     * For regular employees (role === 2):
     *   Count is based on the record's own top-level `status` field.
     *
     * For managers/HR (role !== 2):
     *   PENDING count = records where logged-in employee is the current pending approver in approval_logs.
     *   APPROVED/REJECTED count = records where top-level status matches.
     *
     * ✅ NOTE: reads filter values from `filtersRef` (not `filters` state)
     * so this callback's identity doesn't change on every keystroke.
     */
    const fetchAllStatusCounts = useCallback(async () => {
        try {
            const f = filtersRef.current;
            const params = new URLSearchParams();
            if (isMyTurnOnly) params.append('my_turn', 'true');
            if (f.employee_name) params.append('employee_name', f.employee_name);
            if (f.employee_code) params.append('employee_code', f.employee_code);
            if (f.type) params.append('type', f.type);
            if (f.remarks) params.append('remarks', f.remarks);
            if (f.start_date) params.append('start_date', f.start_date);
            if (f.end_date) params.append('end_date', f.end_date);
            params.append('get_counts', 'true');

            const apiUrl = isEmployeeView
                ? `${backUrl}/api/hr/onduty/?is_employee_view=true&${params.toString()}`
                : `${backUrl}/api/hr/onduty/?${params.toString()}`;

            const res = await axios.get(apiUrl, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });

            if (res.data && typeof res.data === 'object') {
                setStatusCounts({
                    PENDING: res.data.PENDING || 0,
                    APPROVED: res.data.APPROVED || 0,
                    REJECTED: res.data.REJECTED || 0,
                });
            }
        } catch (err) {
            // silently fail — counts are non-critical
        }
    }, [isEmployeeView, isMyTurnOnly, backUrl, authData]);

    /**
     * Checks if the logged-in employee is the current pending approver for a record.
     * Only relevant for managers/HR (role !== 2).
     */
    const isCurrentApprover = (record) => {
        const currentEmployeeId = authData?.employee?.id;
        if (!currentEmployeeId || !Array.isArray(record.approval_logs)) return false;

        return record.approval_logs.some(
            (log) => log.approver === currentEmployeeId && log.status === 'PENDING'
        );
    };

    // ✅ NOTE: reads filter values from `filtersRef` (not `filters` state)
    // so this callback's identity — and therefore any effect depending on
    // it — doesn't change on every keystroke in the filter inputs.
    const fetchOndutyrequests = useCallback(async () => {
        try {
            setLoading(true);

            const f = filtersRef.current;

            const params = new URLSearchParams();
            if (isMyTurnOnly && statusFilter === 'PENDING') params.append('my_turn', 'true');
            if (f.employee_name) params.append('employee_name', f.employee_name);
            if (f.employee_code) params.append('employee_code', f.employee_code);
            if (f.type) params.append('type', f.type);
            if (f.remarks) params.append('remarks', f.remarks);
            if (f.start_date) params.append('start_date', f.start_date);
            if (f.end_date) params.append('end_date', f.end_date);
            params.append('status', statusFilter);

            let apiUrl = isEmployeeView
                ? `${backUrl}/api/hr/onduty/?is_employee_view=true&${params.toString()}`
                : `${backUrl}/api/hr/onduty/?${params.toString()}`;

            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });

            let data = Array.isArray(response.data) ? response.data : [];

            // ✅ CLIENT-SIDE FILTERING — Only for employee view; for HR/GM the backend already
            // returns the correct set (employee own records + full team when TL code is searched).
            if (f.employee_code && f.employee_code.trim()) {
                const codeQuery = f.employee_code.trim().toLowerCase();
                data = data.filter(record => {
                    const displayCode = (
                        record.employee_code ||
                        record.emp_code ||
                        record.employee?.emp_code ||
                        record.employee?.employee_code ||
                        ''
                    ).toString().trim().toLowerCase();

                    return displayCode.includes(codeQuery);
                });
            }

            if (f.employee_name && f.employee_name.trim()) {
                const nameQuery = f.employee_name.trim().toLowerCase();
                data = data.filter(record => {
                    const displayName = (
                        record.employee_name ||
                        `${record.employee?.first_name || ''} ${record.employee?.last_name || ''}` ||
                        ''
                    ).toString().trim().toLowerCase();

                    return displayName.includes(nameQuery);
                });
            }

            if (f.type && f.type.trim()) {
                const typeQuery = f.type.trim().toLowerCase();
                data = data.filter(record => {
                    const displayType = (
                        record.onduty_type ||
                        record.type ||
                        record.check_in_out_mode ||
                        ''
                    ).toString().trim().toLowerCase();
                    return displayType.includes(typeQuery);
                });
            }

            if (f.remarks && f.remarks.trim()) {
                const remarksQuery = f.remarks.trim().toLowerCase();
                data = data.filter(record => {
                    const displayRemarks = (
                        record.remarks ||
                        ''
                    ).toString().trim().toLowerCase();
                    return displayRemarks.includes(remarksQuery);
                });
            }

            if (f.month) {
                const targetMonth = Number(f.month);
                const targetYear = f.year ? Number(f.year) : dayjs().year();
                data = data.filter(record => {
                    const recDate = dayjs(record.start_time || record.created_at || record.date);
                    return recDate.isValid() && (recDate.month() + 1) === targetMonth && recDate.year() === targetYear;
                });
            } else if (f.year) {
                const targetYear = Number(f.year);
                data = data.filter(record => {
                    const recDate = dayjs(record.start_time || record.created_at || record.date);
                    return recDate.isValid() && recDate.year() === targetYear;
                });
            }

            if (f.start_date) {
                const startDateObj = dayjs(f.start_date).startOf('day');
                data = data.filter(record => {
                    const recDate = dayjs(record.start_time || record.created_at || record.date);
                    return recDate.isValid() && (recDate.isAfter(startDateObj.subtract(1, 'second')) || recDate.isSame(startDateObj, 'day'));
                });
            }

            if (f.end_date) {
                const endDateObj = dayjs(f.end_date).endOf('day');
                data = data.filter(record => {
                    const recDate = dayjs(record.end_time || record.start_time || record.created_at || record.date);
                    return recDate.isValid() && (recDate.isBefore(endDateObj.add(1, 'second')) || recDate.isSame(endDateObj, 'day'));
                });
            }

            setOndutyrequests(data);
            setError(null);
            setSelectedRowKeys([]);

        } catch (err) {
            setError(err.response?.data?.message || err.message);
            message.error('Failed to fetch onduty requests');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, isEmployeeView, backUrl, authData]);

    // ✅ CHANGED: this effect no longer depends on `filters` (directly or via
    // fetchOndutyrequests), so typing in the filter inputs no longer
    // auto-triggers a fetch. It now only runs on mount and when something
    // that should immediately refresh the list changes: the status tab,
    // the employee-view toggle, or the backend/auth context becoming ready.
    // Free-text/date/month/year filters are applied only when the user
    // clicks "Filter" (or "Clear").
    useEffect(() => {
        fetchOndutyrequests();
        fetchAllStatusCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, isEmployeeView, backUrl, authData?.access, isMyTurnOnly]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // ✅ NEW: explicit "Filter" action — applies whatever is currently typed
    // into the filter inputs and refetches.
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchOndutyrequests();
        fetchAllStatusCounts();
    };

    const clearFilters = () => {
        const emptyFilters = {
            employee_name: '',
            employee_code: '',
            type: '',
            remarks: '',
            start_date: '',
            end_date: '',
            month: null,
            year: null,
        };
        setFilters(emptyFilters);
        // Update the ref immediately so the fetch below uses the cleared
        // values right away instead of waiting for the sync effect.
        filtersRef.current = emptyFilters;
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchOndutyrequests();
        fetchAllStatusCounts();
    };

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: record.status !== 'PENDING',
        }),
    };

    const showApproveModal = (record) => {
        setSelectedRequest(record);
        setApproveComments('');
        setIsApproveModalVisible(true);
    };

    const showRejectModal = (record) => {
        setSelectedRequest(record);
        setRejectComments('');
        setIsRejectModalVisible(true);
    };

    const showBulkApproveModal = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one request');
            return;
        }
        setBulkApproveComments('');
        setIsBulkApproveModalVisible(true);
    };

    const showBulkRejectModal = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one request');
            return;
        }
        setBulkRejectComments('');
        setIsBulkRejectModalVisible(true);
    };

    const handleApproveOk = async () => {
        try {
            setProcessingId(selectedRequest.id);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "APPROVE",
                    "ids": [selectedRequest.id],
                    "comments": approveComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );

            message.success('Request approved successfully!');
            setIsApproveModalVisible(false);
            setApproveComments('');
            setSelectedRequest(null);
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error approving request: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingId(null);
        }
    };

    const handleRejectOk = async () => {
        try {
            setProcessingId(selectedRequest.id);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "REJECT",
                    "ids": [selectedRequest.id],
                    "comments": rejectComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success('Request rejected successfully!');
            setIsRejectModalVisible(false);
            setRejectComments('');
            setSelectedRequest(null);
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error rejecting request: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingId(null);
        }
    };

    const handleBulkApproveOk = async () => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "APPROVE",
                    "ids": selectedRowKeys,
                    "comments": bulkApproveComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success(`${selectedRowKeys.length} request(s) approved successfully!`);
            setIsBulkApproveModalVisible(false);
            setBulkApproveComments('');
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error approving requests: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBulkRejectOk = async () => {
        try {
            setLoading(true);
            await axios.post(
                `${backUrl}/api/hr/on-duty/bulk-approve/`,
                {
                    "action": "REJECT",
                    "ids": selectedRowKeys,
                    "comments": bulkRejectComments
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            message.success(`${selectedRowKeys.length} request(s) rejected successfully!`);
            setIsBulkRejectModalVisible(false);
            setBulkRejectComments('');
            await fetchOndutyrequests();
            await fetchAllStatusCounts();
        } catch (err) {
            message.error('Error rejecting requests: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleApproveCancel = () => {
        setIsApproveModalVisible(false);
        setApproveComments('');
        setSelectedRequest(null);
    };

    const handleRejectCancel = () => {
        setIsRejectModalVisible(false);
        setRejectComments('');
        setSelectedRequest(null);
    };

    const handleBulkApproveCancel = () => {
        setIsBulkApproveModalVisible(false);
        setBulkApproveComments('');
    };

    const handleBulkRejectCancel = () => {
        setIsBulkRejectModalVisible(false);
        setBulkRejectComments('');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const diffMs = endTime - startTime;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${diffHrs}h ${diffMins}m`;
    };

    const getStatusColor = (status) => {
        const colors = {
            'APPROVED': 'success',
            'REJECTED': 'error',
            'PENDING': 'warning',
            'default': 'default'
        };
        return colors[status] || colors.default;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'APPROVED':
                return <CheckCircleOutlined />;
            case 'PENDING':
                return <ClockCircleOutlined />;
            default:
                return null;
        }
    };

    // ── Export to Excel (respects current filters/status filter + selection) ──
    const exportToExcel = () => {
        const rows = selectedRowKeys.length > 0
            ? ondutyrequests.filter((r) => selectedRowKeys.includes(r.id))
            : ondutyrequests;

        if (!rows.length) {
            message.warning("No data available to export");
            return;
        }

        // Sheet 1: Main On-Duty Requests
        const mainSheetData = rows.map((row) => ({
            "Employee Name": row.employee_name || "-",
            "Employee Code": row.employee_code || "-",
            "Type": row.onduty_type || "-",
            "Mode": row.check_in_out_mode || "-",
            "Start Time": formatDateTime(row.start_time),
            "End Time": formatDateTime(row.end_time),
            "Duration": (row.start_time && row.end_time) ? calculateDuration(row.start_time, row.end_time) : "-",
            "Created At": row.created_at ? dayjs(row.created_at).format("DD/MM/YYYY hh:mm A") : "-",
            "Location": row.location || "-",
            "Status": row.status || "-",
            "Remarks": row.remarks || "-",
        }));

        const ws1 = XLSX.utils.json_to_sheet(mainSheetData);

        // Sheet 2: Approval Logs
        let approvalRows = [];
        rows.forEach((record) => {
            (record.approval_logs || []).forEach((log) => {
                approvalRows.push({
                    "Employee Name": record.employee_name || "-",
                    "Employee Code": record.employee_code || "-",
                    "Approver": log.approver_name || "-",
                    "Status": log.status === "Cancelled" ? "Skipped (On-Duty Cancelled)" : (log.status || "-"),
                    "Remarks": log.remarks || "-",
                    "Actioned At": log.updated_at ? dayjs(log.updated_at).format("DD MMM YYYY, hh:mm A") : "-",
                });
            });
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws1, "OnDuty Requests");

        if (approvalRows.length) {
            const ws2 = XLSX.utils.json_to_sheet(approvalRows);
            XLSX.utils.book_append_sheet(wb, ws2, "Approval Logs");
        }

        XLSX.writeFile(wb, "OnDuty_Requests.xlsx");
        message.success(`Exported ${rows.length} records successfully`);
    };

    const approvalLogsColumns = [
        {
            title: 'Approver',
            dataIndex: 'approver_name',
            key: 'approver_name',
            width: 200,
            render: (name) => (
                <Space>
                    <Text strong>{name}</Text>
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'center',
            render: (status) => (
                <p
                    color={getStatusColor(status)}
                    icon={getStatusIcon(status)}
                    style={{ minWidth: '90px', textAlign: 'center' }}
                >
                    {status}
                </p>
            )
        },
        {
            title: 'Comments',
            dataIndex: 'remarks',
            key: 'comments',
            width: 250,
            render: (comments) => (
                comments ? (
                    <Tooltip placement="topLeft" title={comments}>
                        <Text>{comments}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                )
            )
        },
        {
            title: 'Actioned At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (time) => (
                <Text type="secondary">{formatDateTime(time)}</Text>
            )
        }
    ];

    const expandedRowRender = (record) => {
        return (
            <div style={{ padding: '16px 24px', backgroundColor: '#ffffff' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Table
                            columns={approvalLogsColumns}
                            dataSource={record.approval_logs || []}
                            pagination={false}
                            rowKey={(log) => `${record.id}-${log.level_order}`}
                            size="small"
                            scroll={{ x: 600 }}
                            bordered
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th {...props} style={{
                                            backgroundColor: "#0e497b",
                                            color: 'white',
                                            fontSize: '13px'
                                        }}>
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                        />
                    </div>
                </Space>
            </div>
        );
    };

    const columns = [
        {
            title: 'Employee',
            key: 'employee',
            width: 100,
            render: (record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>
                        {record.employee_name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.employee_code}
                    </Text>
                </Space>
            )
        },
        !isRegularEmployee && {
            title: 'Actions',
            key: 'actions',
            width: 150,
            align: 'center',
            render: (record) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => showApproveModal(record)}
                        loading={processingId === record.id}
                        shape="circle"
                        icon={<CheckOutlined />}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        disabled={record.status !== 'PENDING'}
                    />

                    <Button
                        danger
                        onClick={() => showRejectModal(record)}
                        loading={processingId === record.id}
                        shape="circle"
                        icon={<CloseOutlined />}
                        disabled={record.status !== 'PENDING'}
                    />
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'onduty_type',
            key: 'onduty_type',
            width: 80,
            render: (type, record) => (
                <>
                    <p>{type}</p>
                    <p>{record.check_in_out_mode}</p>
                    {(() => {
                        const raw = record?.lead_data || record?.lead_details || record?.external_lead_details;
                        if (!raw) return false;
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (Array.isArray(parsed)) return parsed.length > 0;
                            if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                            return !!parsed;
                        } catch (e) {
                            return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                        }
                    })() && (
                            <a
                                onClick={() => {
                                    const leadData = record.lead_data || record.lead_details || record.external_lead_details;
                                    // If leadData is a string (some APIs return JSON strings), parse it.
                                    let parsedLeads = leadData;
                                    if (typeof leadData === "string") {
                                        try {
                                            parsedLeads = JSON.parse(leadData);
                                        } catch (e) {
                                            console.error("Failed to parse lead_data", e);
                                        }
                                    }
                                    // Ensure it's an array for the modal to map over
                                    const leadsArray = Array.isArray(parsedLeads) ? parsedLeads : [parsedLeads];
                                    setSelectedLeadDetails(leadsArray);
                                    setShowLead(true);
                                }}
                                style={{ color: "#1677ff", cursor: "pointer", display: "block", marginTop: "4px" }}
                            >
                                Lead Details
                            </a>
                        )}
                </>
            )
        },
        {
            title: 'Start Time',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 100,
            render: (time) => (
                <Space>
                    <Text>{formatDateTime(time)}</Text>
                </Space>
            )
        },
        {
            title: 'End Time',
            dataIndex: 'end_time',
            key: 'end_time',
            width: 100,
            render: (time) => (
                <Space>
                    <Text>{formatDateTime(time)}</Text>
                </Space>
            )
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 380,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <Timeline style={{ marginTop: "12px", marginBottom: "-12px" }}>
                        {logs.map((log, index) => {
                            const statusColor =
                                log.status === "APPROVED"
                                    ? "green"
                                    : log.status === "REJECTED"
                                        ? "red"
                                        : "gray";

                            return (
                                <Timeline.Item key={index} color={statusColor} style={{ paddingBottom: "10px" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "nowrap" }}>
                                            <Text strong style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {log.approver_name}
                                            </Text>
                                            <Tag color={statusColor} style={{ marginRight: 0, whiteSpace: "nowrap", flexShrink: 0 }}>
                                                {log.status === "Cancelled" ? "Skipped (Cancelled)" : log.status}
                                            </Tag>
                                        </div>

                                        {/* Combined Action Details */}
                                        {(log.updated_at || log.remarks) && (
                                            <div style={{ marginTop: "2px" }}>
                                                {log.updated_at && (
                                                    <div style={{ color: "#595959", fontSize: "11px", whiteSpace: "nowrap" }}>
                                                        <Text strong style={{ fontSize: "11px", color: "#595959" }}>
                                                            {log.status === "REJECTED" ? "Rejected On: " : "On: "}
                                                        </Text>
                                                        {dayjs(log.updated_at).format("DD MMM YYYY [at] hh:mm A")}
                                                    </div>
                                                )}
                                                {log.remarks && (
                                                    <div style={{ fontSize: "12px", color: "#262626", wordBreak: "break-word" }}>
                                                        <Text strong>Remark: </Text>
                                                        <Text>{log.remarks}</Text>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Cancellation Block */}
                                        {(log.cancellation_approved_on || log.cancellation_rejected_on || log.cancellation_reason) && (
                                            <div style={{
                                                marginTop: "4px",
                                                paddingTop: "4px",
                                                borderTop: "1px dashed #e8e8e8"
                                            }}>
                                                {(log.cancellation_approved_on || log.cancellation_rejected_on) && (
                                                    <div style={{ color: "#595959", fontSize: "11px", whiteSpace: "nowrap" }}>
                                                        <Text strong style={{ fontSize: "11px", color: "#595959" }}>
                                                            Cancellation {log.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}
                                                        </Text>
                                                        {dayjs(log.cancellation_approved_on || log.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                                                    </div>
                                                )}
                                                {log.cancellation_reason && (
                                                    <div style={{ fontSize: "12px", color: "#262626", wordBreak: "break-word" }}>
                                                        <Text strong>Cancellation Remark: </Text>
                                                        <Text>{log.cancellation_reason}</Text>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                );
            },
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 270,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) || (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude},${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            align: 'center',
            render: (status) => (
                <Space direction="vertical" size={0} align="center">
                    <p>{status}</p>
                </Space>
            )
        },
        {
            title: 'Remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: (remarks) => (
                remarks ? (
                    <Tooltip placement="topLeft" title={remarks}>
                        <Text>{remarks}</Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                )
            )
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            width: 100,
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        // Only show Actions column for non-regular-employees (role !== 2)

    ].filter(Boolean);

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error Loading Requests"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={fetchOndutyrequests}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    // Helper to render a status tab button with a count badge
    const StatusTabButton = ({ status, label, activeColor }) => {
        const isActive = statusFilter === status;
        const count = statusCounts[status] ?? 0;

        const getIcon = () => {
            if (status === "PENDING") return <ClockCircleOutlined />;
            if (status === "APPROVED") return <CheckCircleOutlined />;
            if (status === "REJECTED") return <CloseCircleOutlined />;
            return null;
        };

        return (
            <Badge
                count={count}
                overflowCount={999}
                style={{
                    backgroundColor: isActive ? activeColor : '#d9d9d9',
                    color: isActive ? '#fff' : '#595959',
                    fontWeight: 600,
                    boxShadow: 'none',
                }}
            >
                <Button
                    type={isActive ? "primary" : "default"}
                    onClick={() => setStatusFilter(status)}
                    icon={getIcon()}
                    style={{
                        marginRight: "10px",
                        backgroundColor: isActive ? activeColor : undefined,
                        borderColor: isActive ? activeColor : undefined,
                        minWidth: 120,
                    }}
                >
                    {label}
                </Button>
            </Badge>
        );
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#ffffff', minHeight: '100vh' }}>

            {showLead && (
                <Modal
                    open={showLead}
                    footer={null}
                    onCancel={() => {
                        setShowLead(false);
                        setSelectedLeadDetails(null);
                    }}
                    width={700}
                >
                    {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            {selectedLeadDetails.map((lead, index) => (
                                <Descriptions
                                    key={index}
                                    bordered
                                    column={1}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                    title={selectedLeadDetails.length > 1 ? `Lead ${index + 1}` : null}
                                >
                                    <Descriptions.Item label="Lead Number">
                                        <strong>{lead?.lead_no || lead?.lead_id || "-"}</strong>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Customer Name">
                                        {lead?.customer_name || lead?.name || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Email">
                                        {lead?.email || lead?.customer_email || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Phone Number">
                                        {lead?.number || lead?.phone || lead?.customer_phone || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Project Name">
                                        {lead?.project_name || lead?.project_id || (lead?.project && lead.project.title) || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        <span
                                            style={{
                                                padding: "4px 12px",
                                                borderRadius: "4px",
                                                backgroundColor: "#f6ffed",
                                                color: String(lead?.status?.title || lead?.status || "").toLowerCase().includes("cancel")
                                                    ? "#d46b08"
                                                    : "#52c41a",
                                                fontWeight: 500,
                                                border: "1px solid #b7eb8f"
                                            }}
                                        >
                                            {lead?.status?.title || lead?.status || "-"}
                                        </span>
                                    </Descriptions.Item>
                                </Descriptions>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                            No lead details available
                        </div>
                    )}
                </Modal>
            )}

            <Space direction="vertical" size="large" style={{ width: '100%' }}>

                {/* Status Filter Buttons with Counts */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: "20px", gap: '28px' }}>
                    <StatusTabButton status="PENDING" label="Pending" activeColor="#faad14" />
                    <StatusTabButton status="APPROVED" label="Approved" activeColor="#4CAF50" />
                    <StatusTabButton status="REJECTED" label="Rejected" activeColor="#f5222d" />
                </div>

                {/* Search Filters - Centered */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '16px',
                }}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center',
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        justifyContent: 'center',
                    }}>
                        <Input
                            placeholder="Employee Name"
                            value={filters.employee_name}
                            onChange={(e) => handleFilterChange('employee_name', e.target.value)}
                            onPressEnter={handleApplyFilters}
                            allowClear
                            style={{ width: 160 }}
                        />
                        <Input
                            placeholder="Employee Code"
                            value={filters.employee_code}
                            onChange={(e) => handleFilterChange('employee_code', e.target.value)}
                            onPressEnter={handleApplyFilters}
                            allowClear
                            style={{ width: 140 }}
                        />
                        <Select
                            placeholder="Month"
                            value={filters.month}
                            allowClear
                            style={{ width: 140 }}
                            onChange={(val) => handleFilterChange('month', val)}
                        >
                            {dayjs.months().map((monthName, index) => (
                                <Select.Option key={index + 1} value={index + 1}>
                                    {monthName}
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Year"
                            value={filters.year}
                            allowClear
                            style={{ width: 110 }}
                            onChange={(val) => handleFilterChange('year', val)}
                        >
                            {Array.from({ length: 6 }, (_, i) => dayjs().year() - i).map((y) => (
                                <Select.Option key={y} value={y}>
                                    {y}
                                </Select.Option>
                            ))}
                        </Select>
                        <Input
                            placeholder="Type"
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            onPressEnter={handleApplyFilters}
                            allowClear
                            style={{ width: 120 }}
                        />
                        <Input
                            placeholder="Remarks"
                            value={filters.remarks}
                            onChange={(e) => handleFilterChange('remarks', e.target.value)}
                            onPressEnter={handleApplyFilters}
                            allowClear
                            style={{ width: 140 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>From:</Text>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.start_date}
                                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                                style={{ width: 145 }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>To:</Text>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.end_date}
                                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                                style={{ width: 145 }}
                            />
                        </div>
                        {/* ✅ NEW: explicit Filter button — applies the filters above on click */}
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleApplyFilters}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        >
                            Filter
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={clearFilters}
                        >
                            Clear
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={exportToExcel}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        >
                            Download {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
                        </Button>
                    </div>
                </div>


                {/* Bulk Action Buttons — only for non-regular-employees */}
                {!isRegularEmployee && statusFilter === 'PENDING' && selectedRowKeys.length !== 0 && (
                    <div
                        style={{
                            marginBottom: '16px',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center'
                        }}
                    >
                        <Button
                            type="primary"
                            onClick={showBulkApproveModal}
                            disabled={selectedRowKeys.length === 0}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Bulk Approve
                        </Button>

                        <Button
                            danger
                            onClick={showBulkRejectModal}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Bulk Reject
                        </Button>
                    </div>
                )}

                {/* Spinner overlay wrapping the Table */}
                <Spin
                    spinning={loading}
                    size="large"
                    tip="Loading..."
                    style={{ width: '100%' }}
                >
                    <Table
                        columns={columns}
                        dataSource={ondutyrequests}
                        rowKey="id"
                        loading={false}
                        rowSelection={!isRegularEmployee && statusFilter === 'PENDING' ? rowSelection : null}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: ondutyrequests.length,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setPagination(prev => {
                                    if (prev.pageSize !== pageSize) {
                                        return { current: 1, pageSize };
                                    }
                                    return { current: page, pageSize };
                                });
                            },
                        }}
                        scroll={{ x: 1700 }}
                        rowClassName={(record) => {
                            const targetIdNum = parseInt(targetOndutyId, 10);
                            return record.id === targetIdNum ? "highlighted-row" : "";
                        }}
                        onRow={(record) => {
                            const targetIdNum = parseInt(targetOndutyId, 10);
                            return {
                                ref: record.id === targetIdNum ? highlightRowRef : null,
                            };
                        }}
                        locale={{
                            emptyText: (
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: '16px' }}>
                                        No onduty requests found
                                    </Text>
                                </div>
                            )
                        }}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{
                                        backgroundColor: "#0e497b",
                                        color: 'white'
                                    }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                        bordered
                    />
                </Spin>
            </Space>

            {/* Approve Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                        <span>Approve Request</span>
                    </div>
                }
                open={isApproveModalVisible}
                onOk={handleApproveOk}
                onCancel={handleApproveCancel}
                okText="Approve"
                cancelText="Cancel"
                confirmLoading={processingId === selectedRequest?.id}
                okButtonProps={{
                    style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                }}
            >
                {selectedRequest && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Employee: </Text>
                            <Text>{selectedRequest.employee_name} ({selectedRequest.employee_code})</Text>
                        </div>
                        <div>
                            <Text strong>Type: </Text>
                            <Text>{selectedRequest.onduty_type}</Text>
                        </div>
                        {(selectedRequest.start_time && selectedRequest.end_time) && (
                            <div>
                                <Text strong>Duration: </Text>
                                <Text>{calculateDuration(selectedRequest.start_time, selectedRequest.end_time)}</Text>
                            </div>
                        )}

                        {(() => {
                            const leadData = selectedRequest.lead_data || selectedRequest.lead_details || selectedRequest.external_lead_details;
                            if (leadData) {
                                let parsed = leadData;
                                if (typeof leadData === "string") {
                                    try {
                                        const trimmed = leadData.trim();
                                        if (trimmed === "" || trimmed === "[]" || trimmed === "{}" || trimmed === "null") return null;
                                        parsed = JSON.parse(trimmed);
                                    } catch (e) {
                                        // If not JSON, but has value, proceed
                                    }
                                }
                                if (Array.isArray(parsed) && parsed.length === 0) return null;
                                if (typeof parsed === "object" && parsed !== null && Object.keys(parsed).length === 0) return null;

                                const leads = Array.isArray(parsed) ? parsed : [parsed];
                                return (
                                    <div style={{ padding: "12px", backgroundColor: "#f6ffed", borderRadius: "8px", border: "1px solid #b7eb8f" }}>
                                        <Text strong>Visit Lead Details:</Text>
                                        {leads.map((lead, idx) => (
                                            <div key={idx} style={{ marginTop: 8, fontSize: "12px", borderTop: idx > 0 ? "1px dashed #b7eb8f" : "none", paddingTop: idx > 0 ? 8 : 0 }}>
                                                <div><strong>Customer:</strong> {lead.customer_name || lead.name || "-"}</div>
                                                <div><strong>Project:</strong> {lead.project_name || (lead.project && lead.project.title) || "-"}</div>
                                                <div><strong>Status:</strong> {lead.status?.title || lead.status || "-"}</div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        <div>
                            <Text strong style={{ color: '#ff4d4f' }}>Comments: *</Text>
                            <TextArea
                                rows={4}
                                placeholder="Enter approval comments..."
                                value={approveComments}
                                onChange={(e) => setApproveComments(e.target.value)}
                                maxLength={500}
                                showCount
                                style={{ marginTop: '8px' }}
                            />
                        </div>
                    </Space>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CloseOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                        <span>Reject Request</span>
                    </div>
                }
                open={isRejectModalVisible}
                onOk={handleRejectOk}
                onCancel={handleRejectCancel}
                okText="Reject"
                cancelText="Cancel"
                confirmLoading={processingId === selectedRequest?.id}
                okButtonProps={{ danger: true }}
            >
                {selectedRequest && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text strong>Employee: </Text>
                            <Text>{selectedRequest.employee_name} ({selectedRequest.employee_code})</Text>
                        </div>
                        <div>
                            <Text strong>Type: </Text>
                            <Text>{selectedRequest.onduty_type}</Text>
                        </div>
                        {(selectedRequest.start_time && selectedRequest.end_time) && (
                            <div>
                                <Text strong>Duration: </Text>
                                <Text>{calculateDuration(selectedRequest.start_time, selectedRequest.end_time)}</Text>
                            </div>
                        )}

                        {(() => {
                            const leadData = selectedRequest.lead_data || selectedRequest.lead_details || selectedRequest.external_lead_details;
                            if (leadData) {
                                let parsed = leadData;
                                if (typeof leadData === "string") {
                                    try {
                                        const trimmed = leadData.trim();
                                        if (trimmed === "" || trimmed === "[]" || trimmed === "{}" || trimmed === "null") return null;
                                        parsed = JSON.parse(trimmed);
                                    } catch (e) {
                                        // If not JSON, but has value, proceed
                                    }
                                }
                                if (Array.isArray(parsed) && parsed.length === 0) return null;
                                if (typeof parsed === "object" && parsed !== null && Object.keys(parsed).length === 0) return null;

                                const leads = Array.isArray(parsed) ? parsed : [parsed];
                                return (
                                    <div style={{ padding: "12px", backgroundColor: "#fff1f0", borderRadius: "8px", border: "1px solid #ffa39e" }}>
                                        <Text strong>Visit Lead Details:</Text>
                                        {leads.map((lead, idx) => (
                                            <div key={idx} style={{ marginTop: 8, fontSize: "12px", borderTop: idx > 0 ? "1px dashed #ffa39e" : "none", paddingTop: idx > 0 ? 8 : 0 }}>
                                                <div><strong>Customer:</strong> {lead.customer_name || lead.name || "-"}</div>
                                                <div><strong>Project:</strong> {lead.project_name || (lead.project && lead.project.title) || "-"}</div>
                                                <div><strong>Status:</strong> {lead.status?.title || lead.status || "-"}</div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        <div>
                            <Text strong style={{ color: '#ff4d4f' }}>Reason for Rejection: *</Text>
                            <TextArea
                                rows={4}
                                placeholder="Enter reason for rejection..."
                                value={rejectComments}
                                onChange={(e) => setRejectComments(e.target.value)}
                                maxLength={500}
                                showCount
                                style={{ marginTop: '8px' }}
                            />
                        </div>
                    </Space>
                )}
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Approve Requests</span>
                    </div>
                }
                open={isBulkApproveModalVisible}
                onOk={handleBulkApproveOk}
                onCancel={handleBulkApproveCancel}
                okText="Approve All"
                cancelText="Cancel"
                confirmLoading={loading}
                okButtonProps={{
                    style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                }}
                width={600}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message={`You are about to approve ${selectedRowKeys.length} request(s)`}
                        type="info"
                        showIcon
                    />
                    <div>
                        <Text strong style={{ color: '#ff4d4f' }}>Comments (will be applied to all): *</Text>
                        <TextArea
                            rows={4}
                            placeholder="Enter approval comments for all selected requests..."
                            value={bulkApproveComments}
                            onChange={(e) => setBulkApproveComments(e.target.value)}
                            maxLength={500}
                            showCount
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                </Space>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Reject Requests</span>
                    </div>
                }
                open={isBulkRejectModalVisible}
                onOk={handleBulkRejectOk}
                onCancel={handleBulkRejectCancel}
                okText="Reject All"
                cancelText="Cancel"
                confirmLoading={loading}
                okButtonProps={{ danger: true }}
                width={600}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        message={`You are about to reject ${selectedRowKeys.length} request(s)`}
                        type="warning"
                        showIcon
                    />
                    <div>
                        <Text strong style={{ color: '#ff4d4f' }}>Reason for Rejection (will be applied to all): *</Text>
                        <TextArea
                            rows={4}
                            placeholder="Enter reason for rejection for all selected requests..."
                            value={bulkRejectComments}
                            onChange={(e) => setBulkRejectComments(e.target.value)}
                            maxLength={500}
                            showCount
                            style={{ marginTop: '8px' }}
                        />
                    </div>
                </Space>
            </Modal>
        </div>
    );
}